import React, { useEffect, useMemo, useState } from 'react';
import {
    BuySiteWinRate,
    BuySiteGrid,
    getBuySiteWinRate,
    getMapMatchCounts,
} from '../../services/AnalysisService';
import { mapMappings } from '../../Constants';
import Loading from '../Loading';
import './BuySiteWinRatePanel.css';

type Side = 'all' | 'overall' | 't' | 'ct';

interface Props {
    teamId: string;
    seasons: number[];
    // When provided, locks panel to this map and hides the internal map selector.
    mapName?: string;
    // When provided, locks panel to this side and hides the internal toggle.
    // 'all' or 'overall' renders both T and CT grids.
    side?: Side;
}

const BUY_TYPES: { id: string; label: string }[] = [
    { id: 'pistol', label: 'Pistol' },
    { id: 'eco', label: 'Eco' },
    { id: 'semi-eco', label: 'Semi-Eco' },
    { id: 'semi-buy', label: 'Semi-Buy' },
    { id: 'full-buy', label: 'Full Buy' },
];

const SITES: { id: string; label: string }[] = [
    { id: 'A', label: 'A' },
    { id: 'B', label: 'B' },
    { id: 'none', label: 'Ukendt' },
];

const isAllSide = (s: Side) => s === 'all' || s === 'overall';

const cellBackground = (rounds: number, wins: number): string => {
    if (rounds === 0) return 'transparent';
    const wr = wins / rounds;
    // 0% red → 50% amber → 100% green via HSL hue 0..130
    const hue = Math.round(wr * 130);
    const alpha = Math.min(0.18 + rounds * 0.015, 0.5);
    return `hsla(${hue}, 65%, 45%, ${alpha})`;
};

const formatPct = (rounds: number, wins: number): string =>
    rounds === 0 ? '—' : `${((wins / rounds) * 100).toFixed(0)}%`;

interface GridTableProps {
    title: string;
    grid: BuySiteGrid;
    accent: string;
}

const GridTable: React.FC<GridTableProps> = ({ title, grid, accent }) => {
    const cellFor = (buy: string, site: string) => grid[buy]?.[site] ?? { rounds: 0, wins: 0 };
    const totalRounds = useMemo(() => {
        let n = 0;
        for (const buy of Object.values(grid)) for (const cell of Object.values(buy)) n += cell.rounds;
        return n;
    }, [grid]);

    return (
        <div className="bswr-side">
            <div className="bswr-side-header">
                <span className="bswr-side-pill" style={{ background: accent }}>{title}</span>
                <span className="bswr-side-meta">{totalRounds} runder</span>
            </div>
            {totalRounds === 0 ? (
                <div className="bswr-empty">Ingen runder.</div>
            ) : (
                <table className="bswr-table">
                    <thead>
                        <tr>
                            <th></th>
                            {SITES.map(s => <th key={s.id}>{s.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {BUY_TYPES.map(buy => {
                            const rowTotal = SITES.reduce((sum, s) => sum + cellFor(buy.id, s.id).rounds, 0);
                            if (rowTotal === 0) return null;
                            return (
                                <tr key={buy.id}>
                                    <td className="bswr-row-label">{buy.label}</td>
                                    {SITES.map(s => {
                                        const cell = cellFor(buy.id, s.id);
                                        return (
                                            <td
                                                key={s.id}
                                                className="bswr-cell"
                                                style={{ background: cellBackground(cell.rounds, cell.wins) }}
                                            >
                                                {cell.rounds === 0 ? (
                                                    <span className="bswr-cell-empty">—</span>
                                                ) : (
                                                    <>
                                                        <div className="bswr-cell-pct">{formatPct(cell.rounds, cell.wins)}</div>
                                                        <div className="bswr-cell-sub">{cell.wins}/{cell.rounds}</div>
                                                    </>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

const BuySiteWinRatePanel: React.FC<Props> = ({ teamId, seasons, mapName, side }) => {
    const [internalSide, setInternalSide] = useState<'t' | 'ct'>('t');
    const effectiveSide: Side = side ?? internalSide;
    const showInternalSideToggle = side === undefined;

    const [data, setData] = useState<BuySiteWinRate | null>(null);
    const [loading, setLoading] = useState(false);
    const [maps, setMaps] = useState<{ id: string; label: string; count: number }[]>([]);
    const [internalMap, setInternalMap] = useState<string | undefined>(undefined);
    const showInternalMapSelect = mapName === undefined;
    const selectedMap = mapName ?? internalMap;

    useEffect(() => {
        if (!showInternalMapSelect || !teamId) return;
        let cancelled = false;
        getMapMatchCounts(teamId, seasons).then(counts => {
            if (cancelled) return;
            const entries = Object.entries(counts)
                .map(([id, count]) => ({ id, label: mapMappings.get(id) ?? id, count }))
                .sort((a, b) => b.count - a.count);
            setMaps(entries);
            if (entries.length > 0 && !internalMap) setInternalMap(entries[0].id);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamId, seasons, showInternalMapSelect]);

    useEffect(() => {
        if (!teamId || !selectedMap) { setData(null); return; }
        let cancelled = false;
        setLoading(true);
        getBuySiteWinRate(teamId, selectedMap, seasons)
            .then(d => { if (!cancelled) setData(d); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [teamId, selectedMap, seasons]);

    const T_ACCENT = 'rgba(240, 173, 78, 0.25)';
    const CT_ACCENT = 'rgba(91, 192, 222, 0.25)';

    return (
        <div className="bswr-card">
            {(showInternalSideToggle || showInternalMapSelect) && (
                <div className="bswr-controls">
                    {showInternalMapSelect && maps.length > 0 && (
                        <select
                            className="map-filter-select"
                            value={selectedMap ?? ''}
                            onChange={e => setInternalMap(e.target.value)}
                        >
                            {maps.map(m => (
                                <option key={m.id} value={m.id}>{m.label} ({m.count})</option>
                            ))}
                        </select>
                    )}
                    {showInternalSideToggle && (
                        <div className="bswr-side-toggle">
                            {(['t', 'ct'] as const).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`bswr-side-toggle-btn ${internalSide === p ? 'active' : ''}`}
                                    onClick={() => setInternalSide(p)}
                                >
                                    {p === 't' ? 'T' : 'CT'}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!selectedMap ? (
                <div className="bswr-empty">Vælg et map for at se win-rate matrix.</div>
            ) : loading ? (
                <div className="bswr-loading"><Loading /></div>
            ) : !data ? (
                <div className="bswr-empty">Ingen data.</div>
            ) : (
                <div className={`bswr-grids ${isAllSide(effectiveSide) ? 'bswr-grids-dual' : ''}`}>
                    {(isAllSide(effectiveSide) || effectiveSide === 't') && (
                        <GridTable title="T-side · angreb" grid={data.t} accent={T_ACCENT} />
                    )}
                    {(isAllSide(effectiveSide) || effectiveSide === 'ct') && (
                        <GridTable title="CT-side · forsvar" grid={data.ct} accent={CT_ACCENT} />
                    )}
                </div>
            )}
        </div>
    );
};

export default BuySiteWinRatePanel;
